import { v4 as uuidv4 } from "uuid";
import type { WorkoutExercise, WorkoutSet } from "@/lib/states/workout/store";

export type SessionExerciseRow = {
  id: string;
  name: string;
  ex_order?: number | null;
  sub_order?: number | null;
  tracking_type?: string | null;
  sets?: unknown;
  tempo?: string | null;
  rir?: number | null;
  rest_time?: number | null;
  coach_note?: string | null;
  instructions?: string[] | null;
  primary_muscles?: string[] | null;
  equipment?: string | null;
  experience?: string | null;
  workout_exercise_id?: string | null;
  section_id?: string | null;
};

const getNumeric = (value: unknown): number | undefined =>
  typeof value === "number" && Number.isFinite(value) ? value : undefined;

const getString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

export const createDefaultSet = (
  trackingType?: string | null
): WorkoutSet => {
  if (trackingType === "timed") {
    return { id: uuidv4(), set_type: "working" };
  }
  if (trackingType?.startsWith("distance")) {
    return { id: uuidv4(), set_type: "working" };
  }
  return { id: uuidv4(), startRange: 10, endRange: 12, set_type: "working" };
};

const normalizeTemplateSet = (
  set: unknown,
  trackingType?: string | null
): WorkoutSet => {
  if (!set || typeof set !== "object") {
    return createDefaultSet(trackingType);
  }
  const typed = set as Record<string, unknown>;
  const existingId = getString(typed.id);
  return {
    id: existingId && existingId.length > 0 ? existingId : uuidv4(),
    startRange:
      getNumeric(typed.startRange) ??
      getNumeric(typed.start_range) ??
      undefined,
    endRange:
      getNumeric(typed.endRange) ??
      getNumeric(typed.end_range) ??
      undefined,
    targetWeight:
      getNumeric(typed.targetWeight) ??
      getNumeric(typed.target_weight) ??
      undefined,
    targetTimeSeconds:
      getNumeric(typed.targetTimeSeconds) ??
      getNumeric(typed.target_time_seconds) ??
      undefined,
    targetDistanceMeters:
      getNumeric(typed.targetDistanceMeters) ??
      getNumeric(typed.target_distance_meters) ??
      undefined,
    set_type: getString(typed.set_type) ?? "working",
  };
};

export const buildWorkoutSessionExercises = (
  rows: SessionExerciseRow[]
): WorkoutExercise[][] => {
  const sorted = [...rows].sort(
    (a, b) => (a.ex_order ?? 0) - (b.ex_order ?? 0)
  );
  const grouped: WorkoutExercise[][] = [];

  sorted.forEach((row) => {
    const trackingType = row.tracking_type ?? "strength";
    const templateSets = Array.isArray(row.sets) ? row.sets : [];
    const sets =
      templateSets.length > 0
        ? templateSets.map((set) => normalizeTemplateSet(set, trackingType))
        : [createDefaultSet(trackingType)];

    const exercise: WorkoutExercise = {
      id: row.id,
      workout_exercise_id: row.workout_exercise_id ?? undefined,
      section_id: row.section_id ?? undefined,
      ex_order: row.ex_order ?? 0,
      sub_order: row.sub_order ?? 0,
      name: row.name,
      tracking_type: row.tracking_type,
      tempo: getString(row.tempo) ?? undefined,
      rest_time: getNumeric(row.rest_time) ?? undefined,
      rir: getNumeric(row.rir) ?? undefined,
      coach_note: getString(row.coach_note) ?? undefined,
      instructions: row.instructions ?? undefined,
      primary_muscles: row.primary_muscles ?? undefined,
      equipment: row.equipment ?? undefined,
      experience: row.experience ?? undefined,
      sets,
      notes: null,
    };

    const order = exercise.ex_order ?? 0;
    if (!grouped[order]) {
      grouped[order] = [exercise];
    } else {
      grouped[order].push(exercise);
      grouped[order].sort(
        (a, b) => (a.sub_order ?? 0) - (b.sub_order ?? 0)
      );
    }
  });

  return grouped;
};
